import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useMemo, useState, useEffect, useCallback } from 'react';

import { TabList, TabPanel, TabContext } from '@mui/lab';
// @mui
import { Tab, Grid, Stack, Container, Typography, Pagination } from '@mui/material';

import useResponsiveScreen from 'src/hooks/useResponsiveScreen';

import { genericApiToaster } from 'src/services/utils';
import { fetchUserCount } from 'src/services/user.service';
import { fetchSecureTicketCount } from 'src/services/ticket.service';
import { titleHeadingStyle, titleHeadingStyleSmole } from 'src/pages/style';

import TableToolbar from 'src/components/ToolBars/TableToolbar';
import ActionButton from 'src/components/action-button/action-button';

import DepartmentsList from 'src/sections/department/card/DepartmentsList';
import DepartmentTable from 'src/sections/department/table/DepartmentTable';

import { useAuth } from '../../hooks/useAuth';
import Iconify from '../../components/iconify';
import { useToast } from '../../hooks/useToast';
import AddMembersModal from './modal/AddMembersModal';
import { getComparator } from '../../utils/filterData';
import DepartmentMembersView from './DepartmentMembersPage';
import DepartmentFormModal from './modal/DepartmentFormModal';
import {
  fetchDepartments,
  deleteDepartment,
  createDepartment,
  updateDepartment,
  bulkAssignMembers,
  downloadDepartments,
  getDepartmentImageUrl,
} from '../../services/department.service';

const itemsPerPage = 8;

export default function DepartmentsView() {
  const [tabValue, setTabValue] = useState('1');
  const { t } = useTranslation();
  const { isSmallScreen } = useResponsiveScreen();

  const [viewingMembers, setViewingMembers] = useState(null);

  const [gridPage, setGridPage] = useState(0);
  const [tablePage, setTablePage] = useState(0);
  const [openAddDepartment, setOpenAddDepartment] = useState(false);
  const [departmentData, setDepartmentData] = useState([]);
  const [rowsPerPage, setRowsPerPage] = useState(6);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('id');
  const [isDownloadDebouncing, setIsDownloadDebouncing] = useState(false);
  const [filterName, setFilterName] = useState('');
  const [dataInCSV, setDataInCSV] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Dedicated state to support the horizontal search field selector (Name vs ID)
  const [searchBy, setSearchBy] = useState('name');

  const [openAddMembers, setOpenAddMembers] = useState(false);
  const [selectedDept] = useState(null);
  const [availableUsers] = useState([]);
  const [availableGroups] = useState([]);

  const { showToast, hideToast } = useToast();
  const { permissions, departmentIds: authDeptIds } = useAuth();

  const canCreate = permissions?.includes('DEPARTMENT:CREATE');
  const canDownload = permissions?.includes('CSV:DOWNLOAD');

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  useEffect(() => {
    if (canDownload) {
      downloadDepartments().then((res) => {
        setDataInCSV(res);
      });
    }
  }, [canDownload]);

  const fetchDepartmentsData = async () => {
    setIsLoading(true);
    genericApiToaster(
      fetchDepartments,
      [],
      '',
      t('errorMessages.couldntLoadDep'),
      showToast,
      hideToast,
      t,
      setDepartmentData,
      undefined,
      () => setIsLoading(false)
    );
  };

  useEffect(() => {
    fetchDepartmentsData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authDeptIds]);
  const handleOpenManageMembers = (department) => {
    setViewingMembers(department);
  };

  const handleBulkAssign = (userIds, groupIds) => {
    genericApiToaster(
      bulkAssignMembers,
      [selectedDept.id, { userIds, groupIds }],
      t('successMessages.membersAddedSuccessfully'),
      t('errorMessages.couldntAddMembers'),
      showToast,
      hideToast,
      t,
      () => {
        setOpenAddMembers(false);
        fetchDepartmentsData();
      }
    );
  };

  const addNewDepartment = async (newDepartmentData) =>
    new Promise((resolve, reject) => {
      genericApiToaster(
        createDepartment,
        {
          id: newDepartmentData.id,
          name: newDepartmentData.name,
          description: newDepartmentData.description,
          image: newDepartmentData.image,
          createdBy: newDepartmentData.createdBy,
        },
        t('successMessages.successDepartmentCreate'),
        '',
        showToast,
        hideToast,
        t,
        (createdDept) => {
          fetchDepartmentsData();
          resolve(createdDept);
        },
        (error) => {
          const errorMessage = t(
            error.response?.data === 'Department name already exists'
              ? 'errorMessages.depNameExists'
              : 'errorMessages.couldntCreateDep'
          );
          showToast({
            message: errorMessage,
            severity: 'error',
            props: { hideToast },
          });
          reject(error);
        }
      );
    });

  const modifyDepartmentSuccessCallback = (newDepartmentData) => {
    const newDepartmentList = departmentData.map((department) =>
      department.id === newDepartmentData.id
        ? {
            ...department,
            // Spreads the entire returned object to ensure the fresh 'updatedAt' timestamp is preserved, instantly busting the image cache
            ...newDepartmentData,
          }
        : department
    );
    setDepartmentData(newDepartmentList);
  };

  const modifyDepartment = async (newDepartmentData) => {
    genericApiToaster(
      updateDepartment,
      {
        id: newDepartmentData.id,
        name: newDepartmentData.name,
        description: newDepartmentData.description,
        image: newDepartmentData.image,
        createdBy: newDepartmentData.createdBy,
      },
      t('successMessages.successDepartmentUpdate'),
      '',
      showToast,
      hideToast,
      t,
      (res) => {
        // Fixed: Receives the actual API response containing "HAS_IMAGE" and the new "updatedAt" timestamp
        modifyDepartmentSuccessCallback(res);
      },
      (e) => {
        const errorMessage = t(
          e.response?.data === 'Department name already exists'
            ? 'errorMessages.depNameExists'
            : 'errorMessages.couldntUpdateDep'
        );
        showToast({
          message: errorMessage,
          severity: 'error',
          props: { hideToast },
        });
      }
    );
  };

  const handleDeleteErrorMessages = (e) => {
    const errorMessages = {
      users: t('errorReasons.users'),
      tickets: t('errorReasons.tickets'),
      forms: t('errorReasons.forms'),
    };
    if (e.response?.data) {
      const backendMessage = e.response.data;
      const translatedReasons = backendMessage
        .split(', ')
        .map((reason) => errorMessages[reason] || reason);
      const translatedMessage = t('errorMessages.deleteDepartmentError', {
        reason: translatedReasons.join(', '),
      });
      showToast({ message: translatedMessage, severity: 'error', props: { hideToast } });
    } else {
      showToast({
        message: t('errorMessages.unexpectedError'),
        severity: 'error',
        props: { hideToast },
      });
    }
  };

  const handleDelete = async (id) => {
    genericApiToaster(
      deleteDepartment,
      id,
      t('successMessages.successDepartmentDelete'),
      '',
      showToast,
      hideToast,
      t,
      () => {
        setDepartmentData((prevData) =>
          prevData.filter((department) => department.id !== id)
        );
        setGridPage(0);
      },
      handleDeleteErrorMessages
    );
  };

  const getDeptUserCount = useCallback(
    (departmentId, successCallback) => {
      genericApiToaster(
        fetchUserCount,
        departmentId,
        '',
        t('errorMessages.couldntLoadDepAgents') || "Couldn't load department agents",
        showToast,
        hideToast,
        t,
        successCallback
      );
    },
    [t, showToast, hideToast]
  );

  const getDeptTicketCount = useCallback(
    (departmentId, successCallback) => {
      genericApiToaster(
        fetchSecureTicketCount,
        departmentId,
        '',
        t('errorMessages.couldntLoadDepTickets') || "Couldn't load department tickets",
        showToast,
        hideToast,
        t,
        successCallback
      );
    },
    [t, showToast, hideToast]
  );

  const handleDownloadClick = () => {
    if (!isDownloadDebouncing) {
      setIsDownloadDebouncing(true);
      setTimeout(() => {
        setIsDownloadDebouncing(false);
      }, 1500);
    }
  };

  const handleFilterByName = (event) => {
    setGridPage(0);
    setFilterName(event.target.value);
  };

  const handleChangeRowsPerPage = (event) => {
    setTablePage(0);
    setRowsPerPage(parseInt(event.target.value, 10));
  };

  const handleChangeGridPage = (event, newPage) => {
    setGridPage(newPage - 1);
  };

  const handleChangeTablePage = (event, newPage) => {
    setTablePage(newPage);
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const filteredDepartments = useMemo(() => {
    const baseData = departmentData.filter((d) => (authDeptIds || []).includes(d.id));

    // Performs independent, robust searching based on active search dropdown choice (Name vs ID)
    let result = baseData;
    if (filterName) {
      const query = filterName.toLowerCase().trim();
      result = result.filter((dept) => {
        if (searchBy === 'id') {
          return String(dept.id).includes(query);
        }
        if (searchBy === 'name') {
          return dept.name && dept.name.toLowerCase().includes(query);
        }
        return false;
      });
    }

    const sorted = getComparator(order, orderBy);
    const stabilizedThis = result.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
      const orderCompare = sorted(a[0], b[0]);
      if (orderCompare !== 0) return orderCompare;
      return a[1] - b[1];
    });

    return stabilizedThis
      .map((el) => el[0])
      .map((dept) => {
        const hasCustomImage = dept.image === 'HAS_IMAGE';

        const activeDate = dept.updatedAt || dept.createdAt;
        const version = activeDate ? new Date(activeDate).getTime() : 'default';

        return {
          ...dept,
          image: hasCustomImage
            ? `${getDepartmentImageUrl(dept.id, activeDate)}?v=${version}` // Explicitly appends the version to force instant browser cache-busting
            : '/assets/images/departments/defaultDepartment.png',
        };
      });
  }, [departmentData, authDeptIds, filterName, order, orderBy, searchBy]);

  const totalPages = useMemo(
    () => Math.ceil(filteredDepartments.length / itemsPerPage),
    [filteredDepartments]
  );

  const { isNotFound, searchIsNotFound } = useMemo(() => {
    const isNotFound = !filteredDepartments.length;
    const searchIsNotFound = !filteredDepartments.length && !!filterName;
    return { isNotFound, searchIsNotFound };
  }, [filteredDepartments, filterName]);

  if (viewingMembers) {
    return (
      <DepartmentMembersView
        department={viewingMembers}
        onBack={() => setViewingMembers(null)}
      />
    );
  }

  return (
    <>
      <Helmet>
        <title> {t('nav.departments')} </title>
      </Helmet>
      <DepartmentFormModal
        open={openAddDepartment}
        setOpen={setOpenAddDepartment}
        apiCall={addNewDepartment}
      />

      {selectedDept && (
        <AddMembersModal
          open={openAddMembers}
          onClose={() => setOpenAddMembers(false)}
          department={selectedDept}
          availableUsers={availableUsers}
          availableGroups={availableGroups}
          onSubmit={handleBulkAssign}
        />
      )}

      <Container maxWidth="xl">
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={isSmallScreen ? 2 : 5}
        >
          <Typography
            style={isSmallScreen ? titleHeadingStyleSmole : titleHeadingStyle}
            mx={1}
          >
            {t('nav.departments')}
          </Typography>
          <Grid>
            {canCreate && (
              <ActionButton
                id="add-department-btn"
                onClick={() => setOpenAddDepartment(true)}
                icon={isSmallScreen ? 'octicon:plus-circle-16' : 'eva:plus-fill'}
                text={t('buttons.newDepartment')}
                title={t('buttons.newDepartment')}
                sx={{ mr: 1 }}
              />
            )}
            {canDownload && (
              <ActionButton
                id="download-departments-btn"
                onClick={handleDownloadClick}
                icon={isSmallScreen ? 'octicon:download-16' : 'eva:download-fill'}
                text={t('buttons.download')}
                title={t('buttons.download')}
                href={`data:text/csv;charset=utf-8,${dataInCSV}`}
                disabled={isDownloadDebouncing}
                download="departments.csv"
                sx={{ mx: 1 }}
              />
            )}
          </Grid>
        </Stack>
        <TableToolbar
          numSelected={0}
          filterName={filterName}
          setFilterName={setFilterName}
          filterBy={searchBy} // Binds active search column reference
          setFilterBy={setSearchBy} // Binds active search column setter
          onFilterName={handleFilterByName}
          model={t('nav.departments')}
        />
        <TabContext value={tabValue}>
          <Stack direction="row" sx={{ borderBottom: 1, borderColor: 'divider', mb: 1 }}>
            <TabList onChange={handleTabChange} aria-label="status-tabs">
              <Tab
                onClick={() => setOrderBy('id')}
                icon={<Iconify icon="gridicons:grid" />}
                value="1"
              />
              <Tab icon={<Iconify icon="gridicons:list-unordered" />} value="2" />
            </TabList>
          </Stack>
          <TabPanel value="1">
            <DepartmentsList
              getDeptTicketCount={getDeptTicketCount}
              getDeptUserCount={getDeptUserCount}
              departments={filteredDepartments}
              handleDelete={handleDelete}
              editDepartment={modifyDepartment}
              itemsPerPage={itemsPerPage}
              filterName={filterName}
              isNotFound={isNotFound}
              searchIsNotFound={searchIsNotFound}
              isLoading={isLoading}
              page={gridPage}
              onManageMembers={handleOpenManageMembers}
            />
            {filteredDepartments.length > 0 && (
              <Stack alignItems="center" sx={{ mt: 5 }}>
                <Pagination
                  color="primary"
                  count={totalPages}
                  page={gridPage + 1}
                  onChange={handleChangeGridPage}
                />
              </Stack>
            )}
          </TabPanel>
          <TabPanel value="2">
            <DepartmentTable
              getDeptTicketCount={getDeptTicketCount}
              getDeptUserCount={getDeptUserCount}
              departments={filteredDepartments}
              handleDelete={handleDelete}
              editDepartment={modifyDepartment}
              page={tablePage}
              rowsPerPage={rowsPerPage}
              handleChangeRowsPerPage={handleChangeRowsPerPage}
              handleChangePage={handleChangeTablePage}
              handleRequestSort={handleRequestSort}
              filterName={filterName}
              isNotFound={isNotFound}
              searchIsNotFound={searchIsNotFound}
              order={order}
              orderBy={orderBy}
              isLoading={isLoading}
              onManageMembers={handleOpenManageMembers}
            />
          </TabPanel>
        </TabContext>
      </Container>
    </>
  );
}
